import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Zap, FileText, Mic } from 'lucide-react';
import { useAuth } from '../AuthContext';
import "./ResourcesPage.css";

export default function ResourcesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, showAuthModal } = useAuth();
  const [pendingRedirect, setPendingRedirect] = useState(false);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.getElementById(location.hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    if (user && pendingRedirect) {
      navigate('/generate');
    }
  }, [user, pendingRedirect, navigate]);

  const handleNavigateToGenerate = () => {
    if (user) {
      navigate('/generate');
    } else {
      setPendingRedirect(true);
      showAuthModal('signup');
    }
  };

  return (
    <div className="resources-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <span className="badge">Resources & Support</span>
          <h1>
            Master AI-Powered Presentations with Our
            <span className="gradient-text"> Complete Guide</span>
          </h1>
          <p className="hero-subtitle-resources">
            Learn how to create professional slides from any input - prompts, audio, videos, or documents. 
            From content generation to video export, we've got you covered.
          </p>
          <button className="cta-btn" onClick={handleNavigateToGenerate}>
            <span>Start Creating Now</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 17l9.2-9.2M17 17V7H7"/>
            </svg>
          </button>
        </div>
      </section>

      {/* Modes Section */}
      <section className="modes-section">
        <div className="container">
          <h2>Choose Your Creation</h2>
          <p className="section-subtitle">Two powerful ways to generate professional presentations</p>
          
          <div className="modes-grid">
            {/* Audio to Slide Mode */}
            <div className="mode-card">
              <div className="mode-header audio-mode">
                <Mic size={32} />
                <div>
                  <h3>Audio to Slides HD Video</h3>
                  
                </div>
              </div>
              
              <div className="mode-body">
                <p className="mode-description">
                  Upload audio recordings and get synchronized video presentations with slides that match your narration.
                </p>

                <div className="mode-grid">
                  <div>
                    <h4><Sparkles size={18} /> How It Works</h4>
                    <ol>
                      <li>Upload your audio file (MP3/WAV/MP4)</li>
                      <li>Add optional prompt for specific instructions</li>
                      <li>Cayoty transcribes and generates matching slides</li>
                      <li>Get 1080p video with perfect audio-slide sync</li>
                    </ol>
                  </div>

                  <div>
                    <h4><Zap size={18} /> Audio Features</h4>
                    <div className="feature-boxes">
                      <div className="feature-box">
                        <h5>Audio/Video Only</h5>
                        <p>Upload your file and let Cayoty automatically generate slides</p>
                      </div>
                      <div className="feature-box">
                        <h5>Audio/Video + Instructions</h5>
                        <p>Upload file with custom instructions for specific formatting</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="examples-box">
                  <h4><Zap size={18} /> Audio/Video Only Examples</h4>
                  <div className="example">
                    <code>[Upload meeting recording]</code>
                  </div>
                  <div className="example">
                    <code>[Upload Course recording]</code>
                  </div>
                  <div className="example">
                    <code>[Upload lecture video]</code>
                  </div>
                  <div className="example">
                    <code>[Upload podcast episode]</code>
                  </div>
                  <div className="example">
                    <code>[Upload interview recording]</code>
                  </div>
                  <div className="example">
                    <code>[Upload webinar]</code>
                  </div>
                </div>

                <div className="examples-box">
                  <h4><Zap size={18} /> Audio/Video + Instructions Examples</h4>
                  <div className="example">
                    <code>[Upload meeting recording] + "Create slides with action items and decisions made"</code>
                  </div>
                  <div className="example">
                    <code>[Upload Course recording] + "Create 6 slides 4 bullet points in each slide"</code>
                  </div>
                  <div className="example">
                    <code>[Upload lecture video] + "Make 5 Slides for this tutorial 3 bullet points in each slide"</code>
                  </div>
                  <div className="example">
                    <code>[Upload podcast episode] + "Give me 5 slides highlighting main talking points and quotes"</code>
                  </div>
                  <div className="example">
                    <code>[Upload interview recording] + "Summarize candidate responses and create evaluation slides"</code>
                  </div>
                  <div className="example">
                    <code>[Upload webinar] + "Extract key takeaways and create a summary presentation"</code>
                  </div>
                </div>

                <button className="mode-btn" onClick={handleNavigateToGenerate}>
                  Try Audio Mode
                </button>
              </div>
            </div>

            {/* Prompt to Slide Mode */}
            <div className="mode-card">
              <div className="mode-header prompt-mode">
                <FileText size={32} />
                <div>
                  <h3>Prompt to Slides Presentations</h3>
                  
                </div>
              </div>
              
              <div className="mode-body">
                <p className="mode-description">
                  Describe your presentation in natural language and Cayoty generates professionally designed slides instantly.
                </p>

                <div className="mode-grid">
                  <div>
                    <h4><Sparkles size={18} /> How It Works</h4>
                    <ol>
                      <li>Type what you want in your presentation</li>
                      <li>Cayoty analyzes and structures your content</li>
                      <li>Get slides with layouts, formatting, and visuals</li>
                      <li>Export as PowerPoint, images, or video</li>
                    </ol>
                  </div>

                  <div>
                    <h4><Zap size={18} /> Quick Examples</h4>
                    <div className="example">
                      <strong>Simple:</strong>
                      <code>"Give me 10 slides about the history of the internet, from ARPANET to modern web3"</code>
                    </div>
                    <div className="example">
                      <strong>Detailed:</strong>
                      <code>"Create 5 slides: Slide 1 - Title with company logo, Slide 2 - Problem statement..."</code>
                    </div>
                  </div>
                </div>

                <div className="examples-box">
                  <h4><Zap size={18} /> Text-Based Presentation Examples</h4>
                  <div className="example">
                    <code>"Give me 10 slides about the history of the internet, from ARPANET to modern web3"</code>
                  </div>
                  <div className="example">
                    <code>"Make 8 slides explaining how photosynthesis works for middle school students with diagrams"</code>
                  </div>
                  <div className="example">
                    <code>"Business pitch deck for a sustainable fashion startup seeking $2M seed funding"</code>
                  </div>
                  <div className="example">
                    <code>"Give me 12 slides with a step-by-step tutorial on building a React app from scratch for beginners"</code>
                  </div>
                  <div className="example">
                    <code>"Create 6 slides for quarterly financial review: revenue trends, expenses, and profit margins for Q1 2024"</code>
                  </div>
                </div>

                <div className="examples-box">
                  <h4><Zap size={18} /> Specific Slide-by-Slide Examples</h4>
                  <div className="example">
                    <code>"Create 5 slides: Slide 1 - Title with company logo, Slide 2 - Problem statement, Slide 3 - Our solution, Slide 4 - Market size, Slide 5 - Team bios"</code>
                  </div>
                  <div className="example">
                    <code>"Make 8 slides about coffee brewing methods. Each slide covers one method: espresso, pour-over, French press, cold brew, AeroPress, Moka pot, drip, and Turkish coffee"</code>
                  </div>
                  <div className="example">
                    <code>"Generate a 12-month project timeline presentation. One slide per month showing milestones, deliverables, and team responsibilities"</code>
                  </div>
                </div>

                <div className="examples-box">
                  <h4><Zap size={18} /> Long Content Examples</h4>
                  <div className="example">
                    <strong>Paste Articles/Notes:</strong>
                    <code>"Turn this into slides: [paste your article, notes, or outline]. Cayoty will structure it into a presentation."</code>
                  </div>
                  <div className="example">
                    <strong>Detailed Content:</strong>
                    <code>"Create 8 slides with 3 bullet points each: Climate Change Solutions - A Comprehensive Overview: Problem Statement: Global temperatures have risen 1.1°C..."</code>
                  </div>
                </div>

                <button className="mode-btn" onClick={handleNavigateToGenerate}>
                  Try Prompt Mode
                </button>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="benefits-box">
            <h4>Both Modes Include</h4>
            <ul>
              <li>Professional stock imagery automatically added</li>
              <li>Proper formatting and visual hierarchy</li>
              <li>Multiple export formats (PPTX, PNG, MP4)</li>
              <li>Editable PowerPoint files for final tweaks</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Troubleshooting Section */}
      <section className="troubleshooting-section">
        <div className="container">
          <h2>Quick Troubleshooting</h2>
          <p className="section-subtitle">Common issues and instant solutions</p>

          <div className="faq-grid">
            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>What audio/video formats are supported?</h3>
              </div>
              <p>MP3, WAV, and MP4 files up to 100MB. Audio length limits vary by plan: Free/Starter (10 min), Creator (40 min), Professional (60 min).</p>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>Do I need design skills?</h3>
              </div>
              <p>No! Cayoty automatically handles layouts, formatting, visual hierarchy, and adds professional stock imagery. Just provide your content and get polished slides.</p>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>How many presentations do I get?</h3>
              </div>
              <p>Free: 5/month, Starter: 70/month, Creator: 200/month, Professional: 350/month. Check your Sidebar for current balance and usage.</p>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>Can I edit the generated slides?</h3>
              </div>
              <p>You can upload images and select the designs directly in the platform before exporting. Export as PPTX for full editing of texts in PowerPoint.</p>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>What export formats are available?</h3>
              </div>
              <p>Export as PowerPoint (PPTX), PNG, or HD video. PPTX and PNG exports are unlimited on all plans. Video exports include automatic transitions and timing or duration selection.</p>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>HD Video generation taking too long?</h3>
              </div>
              <p>Video sync generation takes time according to your video length. If it's a 5-minute audio, it will take between 5 to 10 minutes to sync and generate HD video.</p>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>How do I write effective prompts?</h3>
              </div>
              <p>Be specific! Example: "Create a 10-slide pitch deck about renewable energy for investors" works better than "slides about solar". Include your audience, purpose, and key points.</p>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>Can I paste existing content?</h3>
              </div>
              <p>Absolutely! Paste articles, notes, or outlines into the prompt. Cayoty will analyze and structure your content into a formatted presentation with proper slide breaks.</p>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>Audio transcription not accurate?</h3>
              </div>
              <p>Use clear audio with minimal background noise. Speak at a moderate pace and ensure good microphone quality for best results.</p>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>Are my presentations saved?</h3>
              </div>
              <p>Yes! All generations are automatically saved in your dashboard history. Access "My Presentations" anytime to view and download previous creations.</p>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>Slides not generating?</h3>
              </div>
              <p>Ensure your input is within size limits (100MB for audio). Try refreshing the page and generating again. Check your internet connection.</p>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                </svg>
                <h3>Need help or have questions?</h3>
              </div>
              <p>Contact our support team at Support@cayoty.com or use the chat widget. We typically respond within 1 hour during business hours.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}